import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  RefreshCw, Filter, ExternalLink, FileText, AlertCircle, X,
  CheckCircle, Eye, Bell, Trash2, Calendar, MapPin, Building2, DollarSign,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from '../utils/toast';

// ─── Constantes ───────────────────────────────────────────────────────────────
const STATUS = [
  { id: 'nova',            label: 'Novas',          cor: '#3b82f6', icon: AlertCircle },
  { id: 'analisar',        label: 'Analisar',       cor: '#8b5cf6', icon: Eye },
  { id: 'vou_participar',  label: 'Vou Participar', cor: '#10b981', icon: CheckCircle },
  { id: 'monitorar',       label: 'Monitorar',      cor: '#f59e0b', icon: Bell },
  { id: 'descartar',       label: 'Descartar',      cor: '#ef4444', icon: Trash2 },
];

const RELEVANCIA = [
  { id: 'alta',  label: '🟢 Alta',  cor: '#10b981' },
  { id: 'media', label: '🟡 Média', cor: '#f59e0b' },
  { id: 'baixa', label: '🔴 Baixa', cor: '#ef4444' },
];

const PERFIS = [
  { id: 'treinamento',     label: 'Treinamento + SST' },
  { id: 'sistema_gestao',  label: 'Sistema de Gestão' },
];

const UF_LISTA = [
  'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB',
  'PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMoeda = (v) => v == null
  ? '—'
  : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const fmtData = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDataHora = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const diasAteFim = (s) => {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Math.ceil((d - new Date()) / 86400000);
  return diff;
};

// ─── Componente principal ────────────────────────────────────────────────────
export default function Licitacoes({ profissionalAtual, isAdmin }) {
  const [licitacoes, setLicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroRelevancia, setFiltroRelevancia] = useState('todas');
  const [filtroPerfil, setFiltroPerfil] = useState('todos');
  const [filtroUF, setFiltroUF] = useState('todas');
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState('prazo'); // prazo | relevancia | valor | captured
  const [detalheId, setDetalheId] = useState(null);
  const [salvandoId, setSalvandoId] = useState(null);

  // ── Carregar do Supabase ────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('licitacoes_capturadas')
      .select('*')
      .order('captured_at', { ascending: false })
      .limit(500);
    if (error) {
      toast.erro('Não foi possível carregar as licitações. Tente novamente.');
      console.error('[Licitacoes] carregar falhou', error);
      setLicitacoes([]);
    } else {
      setLicitacoes(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Update de status / notas ────────────────────────────────────────────
  const atualizarLicitacao = async (id, patch) => {
    setSalvandoId(id);
    // updated_by é UUID → auth.users(id): registra quem mudou status/notas.
    let userId = null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      userId = session?.user?.id || null;
    } catch { /* sem sessão: grava sem autor */ }
    const patchComAutor = userId ? { ...patch, updated_by: userId } : patch;
    const { error } = await supabase
      .from('licitacoes_capturadas')
      .update(patchComAutor)
      .eq('id', id);
    setSalvandoId(null);
    if (error) {
      toast.erro('Não foi possível salvar a alteração. Tente novamente.');
      console.error('[Licitacoes] update falhou', error);
      return false;
    }
    setLicitacoes(prev => prev.map(l => l.id === id ? { ...l, ...patchComAutor } : l));
    return true;
  };

  // ── Lista filtrada + ordenada ───────────────────────────────────────────
  const filtradas = useMemo(() => {
    let arr = licitacoes;
    if (filtroStatus !== 'todos')    arr = arr.filter(l => l.status === filtroStatus);
    if (filtroRelevancia !== 'todas') arr = arr.filter(l => l.relevancia === filtroRelevancia);
    if (filtroPerfil !== 'todos')    arr = arr.filter(l => l.perfil === filtroPerfil);
    if (filtroUF !== 'todas')        arr = arr.filter(l => l.uf === filtroUF);
    if (busca.trim()) {
      const q = busca.toLowerCase();
      arr = arr.filter(l =>
        (l.orgao_nome || '').toLowerCase().includes(q) ||
        (l.objeto_resumo || '').toLowerCase().includes(q) ||
        (l.municipio || '').toLowerCase().includes(q)
      );
    }
    arr = [...arr];
    if (ordem === 'prazo') {
      arr.sort((a, b) => {
        if (!a.prazo_proposta) return 1;
        if (!b.prazo_proposta) return -1;
        return new Date(a.prazo_proposta) - new Date(b.prazo_proposta);
      });
    } else if (ordem === 'relevancia') {
      const peso = { alta: 0, media: 1, baixa: 2 };
      arr.sort((a, b) => (peso[a.relevancia] ?? 9) - (peso[b.relevancia] ?? 9));
    } else if (ordem === 'valor') {
      arr.sort((a, b) => (Number(b.valor_estimado) || 0) - (Number(a.valor_estimado) || 0));
    } else {
      arr.sort((a, b) => new Date(b.captured_at) - new Date(a.captured_at));
    }
    return arr;
  }, [licitacoes, filtroStatus, filtroRelevancia, filtroPerfil, filtroUF, busca, ordem]);

  // ── Contadores ───────────────────────────────────────────────────────────
  const contadores = useMemo(() => {
    const c = { todos: licitacoes.length };
    STATUS.forEach(s => { c[s.id] = licitacoes.filter(l => l.status === s.id).length; });
    return c;
  }, [licitacoes]);

  const detalhe = detalheId ? licitacoes.find(l => l.id === detalheId) : null;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto', color: '#2d2640' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#1c1530' }}>
            🏛️ Licitações Públicas
          </h1>
          <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4 }}>
            Agente diário · PNCP (Lei 14.133/2021) · Treinamento + SST + Sistemas de Gestão
          </div>
        </div>
        <button
          onClick={carregar}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: '#1f6feb', color: '#fff', border: 'none',
            padding: '8px 14px', borderRadius: 6, cursor: loading ? 'wait' : 'pointer',
            fontSize: 13, fontWeight: 600,
          }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          {loading ? 'Carregando...' : 'Atualizar'}
        </button>
      </div>

      {/* Tabs de status */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap', borderBottom: '1px solid #eeeaf5', paddingBottom: 12 }}>
        <TabBtn
          ativo={filtroStatus === 'todos'}
          onClick={() => setFiltroStatus('todos')}
          label="Todas"
          count={contadores.todos}
          cor="#5f5874"
        />
        {STATUS.map(s => (
          <TabBtn
            key={s.id}
            ativo={filtroStatus === s.id}
            onClick={() => setFiltroStatus(s.id)}
            label={s.label}
            count={contadores[s.id]}
            cor={s.cor}
            Icon={s.icon}
          />
        ))}
      </div>

      {/* Filtros secundários */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Filter size={14} color="#5f5874" />
        <input
          type="text"
          placeholder="Buscar por órgão, objeto, município..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          style={inputStyle({ flex: 1, minWidth: 240 })}
        />
        <select value={filtroRelevancia} onChange={e => setFiltroRelevancia(e.target.value)} style={inputStyle()}>
          <option value="todas">Todas relevâncias</option>
          {RELEVANCIA.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <select value={filtroPerfil} onChange={e => setFiltroPerfil(e.target.value)} style={inputStyle()}>
          <option value="todos">Todos perfis</option>
          {PERFIS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select value={filtroUF} onChange={e => setFiltroUF(e.target.value)} style={inputStyle()}>
          <option value="todas">Todas UFs</option>
          {UF_LISTA.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={ordem} onChange={e => setOrdem(e.target.value)} style={inputStyle()}>
          <option value="prazo">Ordenar: prazo</option>
          <option value="relevancia">Ordenar: relevância</option>
          <option value="valor">Ordenar: valor</option>
          <option value="captured">Ordenar: mais recentes</option>
        </select>
      </div>

      {/* Lista */}
      {loading && licitacoes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#5f5874' }}>Carregando licitações...</div>
      ) : filtradas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#5f5874', background: '#ffffff', borderRadius: 8, border: '1px solid #eeeaf5' }}>
          <FileText size={32} style={{ opacity: 0.4, marginBottom: 12 }} />
          <div>Nenhuma licitação encontrada com esses filtros.</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>
            O agente busca diariamente às 3h BRT (PNCP).
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {filtradas.map(lic => (
            <CardLicitacao
              key={lic.id}
              lic={lic}
              salvando={salvandoId === lic.id}
              onStatus={(novoStatus) => atualizarLicitacao(lic.id, { status: novoStatus })}
              onAbrir={() => setDetalheId(lic.id)}
            />
          ))}
        </div>
      )}

      {/* Detalhe modal */}
      {detalhe && (
        <ModalDetalhe
          lic={detalhe}
          onFechar={() => setDetalheId(null)}
          onStatus={(novoStatus) => atualizarLicitacao(detalhe.id, { status: novoStatus })}
          onNotas={(notas) => atualizarLicitacao(detalhe.id, { notas_internas: notas })}
          onResponsavel={(r) => atualizarLicitacao(detalhe.id, { responsavel: r })}
          salvando={salvandoId === detalhe.id}
          profissionalAtual={profissionalAtual}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Tab de status ───────────────────────────────────────────────────────────
function TabBtn({ ativo, onClick, label, count, cor, Icon }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 12px', borderRadius: 6,
        background: ativo ? `${cor}22` : 'transparent',
        color: ativo ? cor : '#5f5874',
        border: `1px solid ${ativo ? cor : '#e5e0ef'}`,
        cursor: 'pointer', fontSize: 13, fontWeight: ativo ? 700 : 500,
      }}
    >
      {Icon && <Icon size={13} />}
      {label}
      <span style={{
        background: ativo ? cor : '#ebe7f3',
        color: ativo ? '#fff' : '#5f5874',
        padding: '1px 6px', borderRadius: 10,
        fontSize: 10, fontWeight: 700,
      }}>{count ?? 0}</span>
    </button>
  );
}

// ─── Card de licitação ───────────────────────────────────────────────────────
function CardLicitacao({ lic, salvando, onStatus, onAbrir }) {
  const rel = RELEVANCIA.find(r => r.id === lic.relevancia);
  const stat = STATUS.find(s => s.id === lic.status);
  const dias = diasAteFim(lic.prazo_proposta);
  const corPrazo = dias == null ? '#5f5874'
                 : dias < 0      ? '#ef4444'
                 : dias <= 3     ? '#f59e0b'
                 : dias <= 7     ? '#3b82f6'
                                 : '#5f5874';

  return (
    <div style={{
      background: '#ffffff', border: `1px solid #eeeaf5`, borderLeft: `4px solid ${rel?.cor || '#c9c2d8'}`,
      borderRadius: 8, padding: 16,
    }}>
      {/* Linha 1: badges */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, fontSize: 11, fontWeight: 600 }}>
        <span style={{ background: `${rel?.cor || '#5f5874'}22`, color: rel?.cor, padding: '2px 8px', borderRadius: 10 }}>
          {rel?.label || lic.relevancia}
        </span>
        <span style={{ background: '#ebe7f3', color: '#2d2640', padding: '2px 8px', borderRadius: 10 }}>
          {lic.modalidade || 'N/A'}
        </span>
        <span style={{ background: '#ebe7f3', color: '#2d2640', padding: '2px 8px', borderRadius: 10 }}>
          Lei {lic.lei_regente === '13303' ? '13.303/2016' : '14.133/2021'}
        </span>
        {lic.requer_sicaf && (
          <span style={{ background: '#7c2d12', color: '#fdba74', padding: '2px 8px', borderRadius: 10 }}>
            ⚠️ Requer SICAF
          </span>
        )}
        <span style={{ background: '#1e3a5f', color: '#7dd3fc', padding: '2px 8px', borderRadius: 10 }}>
          {lic.perfil === 'sistema_gestao' ? '💻 Sistema' : '🎓 Treinamento'}
        </span>
        {stat && stat.id !== 'nova' && (
          <span style={{ background: `${stat.cor}33`, color: stat.cor, padding: '2px 8px', borderRadius: 10, marginLeft: 'auto' }}>
            {stat.label}
          </span>
        )}
      </div>

      {/* Linha 2: órgão */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Building2 size={14} color="#5f5874" />
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1c1530' }}>{lic.orgao_nome}</span>
        {lic.uf && (
          <span style={{ fontSize: 12, color: '#5f5874', display: 'flex', alignItems: 'center', gap: 2 }}>
            <MapPin size={12} /> {lic.uf}{lic.municipio ? ` · ${lic.municipio}` : ''}
          </span>
        )}
      </div>

      {/* Objeto */}
      <div style={{ fontSize: 13, color: '#2d2640', marginBottom: 10, lineHeight: 1.5 }}>
        {(lic.objeto_resumo || '').slice(0, 280)}{lic.objeto_resumo && lic.objeto_resumo.length > 280 ? '…' : ''}
      </div>

      {/* Métricas */}
      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#5f5874', marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <DollarSign size={12} /> <b style={{ color: '#2d2640' }}>{fmtMoeda(lic.valor_estimado)}</b>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: corPrazo }}>
          <Calendar size={12} />
          Prazo: <b>{fmtDataHora(lic.prazo_proposta)}</b>
          {dias != null && (
            <span style={{ background: `${corPrazo}33`, padding: '1px 6px', borderRadius: 8, fontSize: 11 }}>
              {dias < 0 ? `${Math.abs(dias)}d atrasado` : dias === 0 ? 'hoje' : `em ${dias}d`}
            </span>
          )}
        </span>
        {lic.palavras_match?.length > 0 && (
          <span style={{ fontSize: 11, color: '#5f5874' }}>
            kw: {lic.palavras_match.slice(0, 3).join(', ')}
          </span>
        )}
      </div>

      {/* Ações */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: '1px solid #eeeaf5', paddingTop: 10 }}>
        {STATUS.filter(s => s.id !== 'nova').map(s => {
          const Icon = s.icon;
          const ativo = lic.status === s.id;
          return (
            <button
              key={s.id}
              onClick={() => onStatus(s.id)}
              disabled={salvando}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 10px', borderRadius: 6,
                background: ativo ? `${s.cor}33` : 'transparent',
                color: ativo ? s.cor : '#5f5874',
                border: `1px solid ${ativo ? s.cor : '#e5e0ef'}`,
                cursor: salvando ? 'wait' : 'pointer', fontSize: 12, fontWeight: ativo ? 700 : 500,
              }}
            >
              <Icon size={12} /> {s.label}
            </button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button
            onClick={onAbrir}
            style={{
              padding: '6px 12px', borderRadius: 6,
              background: '#ffffff', color: '#7dd3fc',
              border: '1px solid #1e3a5f', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}
          >
            Ver análise IA
          </button>
          {lic.link_pncp && (
            <a
              href={lic.link_pncp}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 6,
                background: '#1f6feb', color: '#fff',
                textDecoration: 'none', fontSize: 12, fontWeight: 600,
              }}
            >
              <ExternalLink size={12} /> PNCP
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal de detalhe ────────────────────────────────────────────────────────
function ModalDetalhe({ lic, onFechar, onStatus, onNotas, onResponsavel, salvando, profissionalAtual }) {
  const [notas, setNotas] = useState(lic.notas_internas || '');
  const [responsavel, setResponsavel] = useState(lic.responsavel || profissionalAtual || '');
  const ai = lic.analise_ia || {};

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 20, overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10,
          maxWidth: 800, width: '100%', padding: 24, color: '#2d2640',
          marginTop: 40, marginBottom: 40,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #eeeaf5' }}>
          <div>
            <h2 style={{ fontSize: 18, margin: 0, color: '#1c1530' }}>{lic.orgao_nome}</h2>
            <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4 }}>
              {lic.modalidade} · {lic.uf || '?'}{lic.municipio ? ` · ${lic.municipio}` : ''} · Lei {lic.lei_regente === '13303' ? '13.303/2016' : '14.133/2021'}
            </div>
          </div>
          <button onClick={onFechar} style={{ background: 'transparent', border: 'none', color: '#5f5874', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Objeto completo */}
        <Section titulo="Objeto">
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>{lic.objeto_completo || lic.objeto_resumo}</div>
        </Section>

        {/* Métricas */}
        <Section titulo="Dados do edital">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, fontSize: 13 }}>
            <Info label="Valor estimado" valor={fmtMoeda(lic.valor_estimado)} />
            <Info label="Data publicação" valor={fmtData(lic.data_publicacao)} />
            <Info label="Data abertura" valor={fmtDataHora(lic.data_abertura)} />
            <Info label="Prazo proposta" valor={fmtDataHora(lic.prazo_proposta)} />
            <Info label="Modo disputa" valor={lic.modo_disputa || '—'} />
            <Info label="CNPJ órgão" valor={lic.orgao_cnpj || '—'} />
          </div>
        </Section>

        {/* Análise IA */}
        {ai && Object.keys(ai).length > 0 && !ai.erro && (
          <Section titulo="🤖 Análise da IA">
            {ai.justificativa && (
              <div style={{ fontSize: 13, color: '#2d2640', marginBottom: 12, padding: 12, background: '#ffffff', borderRadius: 6, borderLeft: '3px solid #1f6feb' }}>
                {ai.justificativa}
              </div>
            )}
            {ai.objeto_estruturado && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, fontSize: 12, marginBottom: 12 }}>
                <Info label="Tipo de serviço" valor={ai.objeto_estruturado.tipo_servico || '—'} />
                <Info label="Público alvo" valor={ai.objeto_estruturado.publico_alvo_estimado || '—'} />
                <Info label="Duração estimada" valor={ai.objeto_estruturado.duracao_estimada || '—'} />
                <Info label="Normas envolvidas" valor={(ai.objeto_estruturado.normas_envolvidas || []).join(', ') || '—'} />
              </div>
            )}
            {Array.isArray(ai.checklist_habilitacao) && ai.checklist_habilitacao.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#5f5874', marginBottom: 6 }}>📋 Checklist habilitação</div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                  {ai.checklist_habilitacao.map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{item}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(ai.riscos) && ai.riscos.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', marginBottom: 6 }}>⚠️ Riscos identificados</div>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                  {ai.riscos.map((item, i) => <li key={i} style={{ marginBottom: 4 }}>{item}</li>)}
                </ul>
              </div>
            )}
          </Section>
        )}

        {ai?.erro && (
          <Section titulo="⚠️ Falha na IA">
            <div style={{ fontSize: 12, color: '#fdba74' }}>
              A classificação automática falhou. Análise manual recomendada. Detalhe: {ai.erro}
            </div>
          </Section>
        )}

        {/* Notas internas */}
        <Section titulo="📝 Notas internas">
          <textarea
            value={notas}
            onChange={e => setNotas(e.target.value)}
            onBlur={() => notas !== (lic.notas_internas || '') && onNotas(notas)}
            placeholder="Suas anotações sobre essa licitação..."
            style={{
              width: '100%', minHeight: 80, padding: 10,
              background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6,
              color: '#2d2640', fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
            }}
          />
        </Section>

        {/* Responsável */}
        <Section titulo="Responsável">
          <input
            type="text"
            value={responsavel}
            onChange={e => setResponsavel(e.target.value)}
            onBlur={() => responsavel !== (lic.responsavel || '') && onResponsavel(responsavel)}
            placeholder="Quem está acompanhando essa licitação?"
            style={inputStyle({ width: '100%' })}
          />
        </Section>

        {/* Ações */}
        <div style={{ display: 'flex', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid #eeeaf5', flexWrap: 'wrap' }}>
          {STATUS.filter(s => s.id !== 'nova').map(s => {
            const Icon = s.icon;
            const ativo = lic.status === s.id;
            return (
              <button
                key={s.id}
                onClick={() => onStatus(s.id)}
                disabled={salvando}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 6,
                  background: ativo ? s.cor : 'transparent',
                  color: ativo ? '#fff' : s.cor,
                  border: `1px solid ${s.cor}`,
                  cursor: salvando ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                <Icon size={14} /> {s.label}
              </button>
            );
          })}
        </div>

        {/* Links externos */}
        {(lic.link_pncp || lic.link_edital) && (
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            {lic.link_pncp && (
              <a href={lic.link_pncp} target="_blank" rel="noopener noreferrer"
                 style={linkBtnStyle}>
                <ExternalLink size={12} /> Edital no PNCP
              </a>
            )}
            {lic.link_edital && lic.link_edital !== lic.link_pncp && (
              <a href={lic.link_edital} target="_blank" rel="noopener noreferrer"
                 style={linkBtnStyle}>
                <ExternalLink size={12} /> Sistema de origem
              </a>
            )}
            <a href="https://www.gov.br/compras/pt-br" target="_blank" rel="noopener noreferrer"
               style={linkBtnStyle}>
              <ExternalLink size={12} /> Compras.gov.br
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-componentes ─────────────────────────────────────────────────────────
function Section({ titulo, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#5f5874', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        {titulo}
      </div>
      {children}
    </div>
  );
}

function Info({ label, valor }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#5f5874', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#1c1530', marginTop: 2 }}>{valor}</div>
    </div>
  );
}

// ─── Estilos auxiliares ──────────────────────────────────────────────────────
const inputStyle = (extra = {}) => ({
  background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6,
  color: '#2d2640', padding: '7px 10px', fontSize: 13, ...extra,
});

const linkBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', borderRadius: 6,
  background: '#1f6feb', color: '#fff',
  textDecoration: 'none', fontSize: 12, fontWeight: 600,
};
